import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Smartphone, Trash2, RefreshCw, Battery, Signal, Wifi } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { odpowiedzi: 'Odpowiedzi', rezerwowy: 'Rezerwowy', wysylka: 'Wysylka' };
const ROLE_COLORS = { odpowiedzi: 'bg-blue-500/10 text-blue-400', rezerwowy: 'bg-yellow-500/10 text-yellow-400', wysylka: 'bg-green-500/10 text-green-400' };

export default function PhoneGatewaysPage() {
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPhones = useCallback(async () => {
    try {
      const r = await api.get('/phone-gateways');
      setPhones(r.data.items || []);
    } catch { toast.error('Blad ladowania telefonow'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPhones(); const iv = setInterval(fetchPhones, 15000); return () => clearInterval(iv); }, [fetchPhones]);

  const handleRoleChange = async (phoneId, role) => {
    try {
      await api.put(`/phone-gateways/${phoneId}/role`, { role });
      toast.success(`Rola zmieniona na: ${ROLE_LABELS[role]}`);
      fetchPhones();
    } catch { toast.error('Blad'); }
  };

  const handleDelete = async (phoneId) => {
    if (!window.confirm('Usunac telefon z systemu?')) return;
    try {
      await api.delete(`/phone-gateways/${phoneId}`);
      toast.success('Usunieto');
      fetchPhones();
    } catch { toast.error('Blad'); }
  };

  const isOnline = (phone) => {
    if (!phone.last_heartbeat) return false;
    const diff = (Date.now() - new Date(phone.last_heartbeat).getTime()) / 1000;
    return diff < 120;
  };

  return (
    <div className="space-y-6" data-testid="phone-gateways-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Smartphone className="h-6 w-6 text-primary" />
            Telefony Android
          </h1>
          <p className="text-muted-foreground text-sm">Bramki SMS na telefonach z aplikacja Torr Gateway</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPhones} data-testid="refresh-phones">
          <RefreshCw className="h-4 w-4 mr-2" /> Odswiez
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : phones.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Smartphone className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Brak podlaczonych telefonow</p>
              <p className="text-sm mt-2">Zainstaluj aplikacje Torr Gateway na telefonie Android i polacz z PIN-em.</p>
              <p className="text-xs mt-4 text-muted-foreground">APK do pobrania: <a href="https://torr.pl/download/TorrGateway.apk" className="text-primary underline">torr.pl/download/TorrGateway.apk</a></p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>PIN</TableHead>
                  <TableHead>SIM 1</TableHead>
                  <TableHead>SIM 2</TableHead>
                  <TableHead>Bateria</TableHead>
                  <TableHead>Statystyki</TableHead>
                  <TableHead>Rola</TableHead>
                  <TableHead>Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phones.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${isOnline(p) ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                        <span className="text-xs">{isOnline(p) ? 'Online' : 'Offline'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-sm">{p.device_model || '?'}</TableCell>
                    <TableCell><code className="text-xs bg-muted px-2 py-1 rounded">{p.pin}</code></TableCell>
                    <TableCell className="text-xs">
                      {p.sim1_number ? (
                        <div>
                          <div className="font-mono">{p.sim1_number}</div>
                          <div className="text-muted-foreground">{p.sim1_operator || ''}</div>
                        </div>
                      ) : <span className="text-muted-foreground">brak</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {p.sim2_number ? (
                        <div>
                          <div className="font-mono">{p.sim2_number}</div>
                          <div className="text-muted-foreground">{p.sim2_operator || ''}</div>
                        </div>
                      ) : <span className="text-muted-foreground">brak</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs">
                        <Battery className="h-3.5 w-3.5" />
                        {p.battery_pct != null ? `${p.battery_pct}%` : '?'}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>SMS: {p.sms_sent || 0} / {p.sms_received || 0}</div>
                      <div className="text-muted-foreground">Tel: {p.calls_made || 0}</div>
                    </TableCell>
                    <TableCell>
                      <Select value={p.role || 'odpowiedzi'} onValueChange={v => handleRoleChange(p.id, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="odpowiedzi">Odpowiedzi</SelectItem>
                          <SelectItem value="rezerwowy">Rezerwowy</SelectItem>
                          <SelectItem value="wysylka">Wysylka</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(p.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
